import React, { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import VatDiscountReview from './VatDiscountReview'
import VatEnquiryPrices from './VatEnquiryPrices'
import VatRecovery from './VatRecovery'

export default function VatJobWorkflow(props) {
  const { job, profile, customer } = props
  const scope = JSON.stringify([job.id, job.workshop_id, job.customer_id, job.financial_revision_key,
    job.active_payment_attempt_id, job.active_payment_bundle_attempt_id, profile?.id, profile?.role,
    profile?.workshop_id, customer?.email])
  return <ScopedVatJobWorkflow key={scope} {...props} />
}

function ScopedVatJobWorkflow({ job, profile, customer, allowQuote = true, onDiscountApplied, onPricesApplied }) {
  const mounted = useRef(true)
  const paymentSequence = useRef(0)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; paymentSequence.current++ } }, [])
  const [context,setContext] = useState(null)
  const [error,setError] = useState('')
  const [notice,setNotice] = useState('')
  const [busy,setBusy] = useState(false)
  const [to,setTo] = useState(customer?.email || '')
  const [name,setName] = useState([customer?.first_name,customer?.last_name].filter(Boolean).join(' '))
  const [address,setAddress] = useState(customer?.address || '')
  const [legalEvidence,setLegalEvidence] = useState('')
  const [taxDate,setTaxDate] = useState('')
  const [taxEvidence,setTaxEvidence] = useState('')
  const [agreedAt,setAgreedAt] = useState('')
  const [agreementEvidence,setAgreementEvidence] = useState('')
  const agreementRequest = useRef(null)
  const preparationRequest = useRef(null)
  const [prepared,setPrepared] = useState(false)
  const [url,setUrl] = useState('')
  const [paymentPreview,setPaymentPreview] = useState(null)
  const owner = profile?.role === 'owner' && profile.workshop_id === job.workshop_id
  const reload = useCallback(async () => {
    const { data,error:err } = await supabase.rpc('workshop_vat_job_status',{p_job_id:job.id})
    if (err) throw new Error(err.message)
    if (!data || data.job_id !== job.id) throw new Error('Reviewed job details are unavailable.')
    if (mounted.current) setContext(data)
  }, [job.id])
  useEffect(() => { reload().catch(err=>{if (mounted.current) setError(err.message)}) }, [reload])
  async function action(fn) {
    setBusy(true);setError('');setNotice('')
    try { await fn();if (mounted.current) await reload() } catch(err) { if (mounted.current) setError(err.message) } finally { if (mounted.current) setBusy(false) }
  }
  async function request(endpoint,body) {
    const {data}=await supabase.auth.getSession()
    if (!mounted.current) throw new Error('Job context changed. Review again.')
    if (!data?.session?.access_token) throw new Error('Please sign in again.')
    const res=await fetch('/.netlify/functions/'+endpoint,{method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${data.session.access_token}`},body:JSON.stringify({jobId:job.id,...body})})
    const value=await res.json()
    if (!res.ok) throw new Error(value.error || 'Action could not be completed.')
    if (!mounted.current) throw new Error('Job context changed. Review again.')
    return value
  }
  function invalidatePayment() {
    paymentSequence.current++
    setPaymentPreview(null)
  }
  async function paymentAction(fn) {
    invalidatePayment()
    const sequence = paymentSequence.current
    setBusy(true);setError('');setNotice('')
    try {
      const result = await fn()
      if (!mounted.current || sequence !== paymentSequence.current) return
      await reload()
      if (mounted.current && sequence === paymentSequence.current && result) setPaymentPreview(result)
    } catch (err) {
      if (mounted.current && sequence === paymentSequence.current) {
        setPaymentPreview(null)
        setError(err.message)
      }
    } finally { if (mounted.current) setBusy(false) }
  }
  async function previewPayment() {
    const result = await request('send-payment-reminder', { action:'preview', to })
    if (!result.delivery_id || result.recipient?.trim().toLowerCase() !== to.trim().toLowerCase()
      || !Array.isArray(result.attachments) || !['prepared','sent','sending','uncertain','failed'].includes(result.delivery_state)) {
      throw new Error('Payment preview is unavailable. Review again before sending.')
    }
    return result
  }
  async function sendPayment() {
    const preview = paymentPreview
    if (!preview || preview.delivery_state !== 'prepared') throw new Error('A fresh payment preview is required.')
    const result = await request('send-payment-reminder', { action:'send', to, deliveryId:preview.delivery_id })
    if (result.delivery_state !== 'sent') throw new Error('Delivery outcome is uncertain. Check the recorded status before taking further action.')
    return { ...preview, delivery_state:'sent' }
  }
  async function recordAgreement() {
    const command = { job_id:job.id, financial_revision_key:context?.financial_revision_key || job.financial_revision_key,
      agreed_at:new Date(agreedAt).toISOString(), evidence:agreementEvidence.trim() }
    const fingerprint = JSON.stringify(command)
    if (agreementRequest.current?.fingerprint !== fingerprint) agreementRequest.current = { fingerprint, key:crypto.randomUUID() }
    const {error:err} = await supabase.rpc('record_workshop_vat_agreement', {
      p_workshop_id:job.workshop_id, p_idempotency_key:agreementRequest.current.key, p_command:command,
    })
    if (err) throw new Error(err.message)
    setNotice('Customer agreement recorded. An invoice has not been issued.')
  }
  async function report(issueInvoice=false) {
    const fingerprint = JSON.stringify([taxDate,taxEvidence])
    if (preparationRequest.current?.fingerprint !== fingerprint) preparationRequest.current = { fingerprint, key:crypto.randomUUID() }
    const result=await request(job.module_key==='general'?'create-general-report-version':'create-report-version',{taxDate,taxDateEvidence:taxEvidence,issueInvoice,preparationKey:preparationRequest.current.key})
    setUrl(result.signed_url || '');setPrepared(true)
    setNotice(result.issued?'The recorded invoice is issued. Its frozen report is ready to view.':'Invoice and report prepared. Review the PDF before issuing.')
  }
  return <section className="card" aria-label="Reviewed quote and invoice" style={{padding:16,margin:'12px 0'}}>
    <h3>Reviewed quote and invoice</h3>
    <p>Uses the recorded service prices. Save workshop notes before preparing the report.</p>
    {error && <div role="alert" className="alert alert-error">{error}</div>}
    {notice && <div role="status">{notice}</div>}
    {context?.enquiry_requires_prices && <>
      {owner ? <VatEnquiryPrices key={context.financial_revision_key} job={job} services={context.services} onApplied={async data=>{await reload();if(mounted.current){setNotice('Assessment prices saved. Customer agreement is still required.');if(onPricesApplied)onPricesApplied(data.job)}}} /> : <p>The owner must review the assessed prices before a quote or agreement.</p>}
    </>}
    {context && !context.enquiry_requires_prices && <>
      <p>{context.invoice?'Invoice issued':context.agreement?'Customer agreement recorded':'Customer agreement required'}</p>
      {owner && !context.agreement && !context.invoice && context.prices?.tax?.complete && <VatDiscountReview key={context.financial_revision_key || job.financial_revision_key} job={job} context={context} onApplied={async data=>{invalidatePayment();await reload();if(mounted.current && onDiscountApplied)await onDiscountApplied(data)}} />}
      {allowQuote && !context.agreement && <>
        <div className="field"><label>Quote recipient<input type="email" value={to} onChange={e=>setTo(e.target.value)} /></label></div>
        <button className="btn btn-primary btn-sm" type="button" disabled={busy || !to} onClick={()=>action(async()=>{await request('send-quote',{to});setNotice('Quote delivery recorded.')})}>Send reviewed quote</button>
      </>}
      {owner && !context.agreement && !context.invoice && <fieldset disabled={busy}>
        <legend>Record an existing customer agreement</legend>
        <p>Confirm when and how the customer agreed to the recorded services and total.</p>
        <div className="field"><label>Customer agreed at<input type="datetime-local" value={agreedAt} onChange={e=>setAgreedAt(e.target.value)} /></label></div>
        <div className="field"><label>Evidence of customer agreement<textarea maxLength={1000} value={agreementEvidence} onChange={e=>setAgreementEvidence(e.target.value)} /></label></div>
        <button className="btn btn-primary btn-sm" type="button" disabled={busy || !agreedAt || agreementEvidence.trim().length < 5} onClick={()=>action(recordAgreement)}>Record customer agreement</button>
      </fieldset>}
      {context.invoice && <button className="btn btn-primary btn-sm" type="button" disabled={busy} onClick={()=>action(()=>report())}>View recorded invoice and report</button>}
      {owner && context.invoice && <fieldset disabled={busy}>
        <legend>Payment request with issued report</legend>
        <div className="field"><label>Payment recipient<input type="email" value={to} onChange={e=>{invalidatePayment();setTo(e.target.value)}} /></label></div>
        <button className="btn btn-primary btn-sm" type="button" onClick={()=>paymentAction(async()=>{await request('create-payment-link',{})})}>Prepare payment link</button>
        <button className="btn btn-primary btn-sm" type="button" disabled={busy || !to} onClick={()=>paymentAction(previewPayment)}>Preview payment request and report</button>
        {paymentPreview && <>
          <p role="status">{paymentPreview.delivery_state === 'sent' ? 'Payment request and frozen report delivery recorded.' : paymentPreview.delivery_state === 'prepared' ? 'Payment preview saved. No email sent.' : 'Delivery outcome needs review. Sending and discard are unavailable.'}</p>
          <p>To: {paymentPreview.recipient} — {paymentPreview.subject}</p>
          <iframe title="Frozen payment email preview" sandbox="" srcDoc={paymentPreview.html} style={{width:'100%',height:420,border:'1px solid #ddd'}} />
          <p>Attached: {paymentPreview.attachments.map(a=>a.filename).join(', ')}</p>
          {paymentPreview.delivery_state==='prepared' && <button className="btn btn-secondary btn-sm" type="button" disabled={busy} onClick={()=>paymentAction(async()=>{
            await request('send-payment-reminder',{action:'discard',to,deliveryId:paymentPreview.delivery_id})
          })}>Discard unsent payment preview</button>}
          <button className="btn btn-primary btn-sm" type="button" disabled={busy || paymentPreview.delivery_state!=='prepared'} onClick={()=>paymentAction(sendPayment)}>Send previewed payment request</button>
        </>}
      </fieldset>}
      {owner && context.agreement && !context.invoice && <>
        {!context.customer_legal && <fieldset disabled={busy}><legend>Customer invoice details</legend>
          <div className="field"><label>Legal name<input value={name} onChange={e=>setName(e.target.value)} /></label></div>
          <div className="field"><label>Invoice address<textarea value={address} onChange={e=>setAddress(e.target.value)} /></label></div>
          <div className="field"><label>How these details were confirmed<input value={legalEvidence} onChange={e=>setLegalEvidence(e.target.value)} /></label></div>
          <button className="btn btn-primary btn-sm" type="button" onClick={()=>action(async()=>{const {error:err}=await supabase.rpc('save_workshop_vat_customer_legal',{p_workshop_id:job.workshop_id,p_customer_id:job.customer_id,p_name:name,p_address:address,p_evidence:legalEvidence});if(err)throw new Error(err.message)})}>Confirm invoice details</button>
        </fieldset>}
        {context.customer_legal && <>
          <div className="field"><label>Tax date<input type="date" value={taxDate} onChange={e=>{setTaxDate(e.target.value);setPrepared(false)}} /></label></div>
          <div className="field"><label>Evidence for this tax date<textarea value={taxEvidence} onChange={e=>{setTaxEvidence(e.target.value);setPrepared(false)}} /></label></div>
          <button className="btn btn-primary btn-sm" type="button" disabled={busy || !taxDate || !taxEvidence} onClick={()=>action(()=>report())}>Prepare invoice and report</button>
          {prepared && <button className="btn btn-primary btn-sm" type="button" disabled={busy} onClick={()=>action(()=>report(true))}>Issue reviewed invoice</button>}
        </>}
      </>}
    </>}
    {url && <p><a href={url} target="_blank" rel="noreferrer">Open frozen invoice and report PDF</a></p>}
    {owner && <VatRecovery job={job} profile={profile} onChanged={async()=>{preparationRequest.current=null;setPrepared(false);invalidatePayment();await reload()}} />}
  </section>
}
